import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'utils/axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import CrownOutlined from '@ant-design/icons/CrownOutlined';

export default function AdminProfile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Obter dados do admin a partir do localStorage
  let adminData = { name: 'Super Administrador', email: 'admin@kambatime.com' };
  try {
    const raw = localStorage.getItem('gpo_admin_user');
    if (raw) adminData = JSON.parse(raw);
  } catch (e) {}

  // Iniciais do nome (ex.: "KambaTime Superadmin" → "KS")
  const initials =
    (adminData.name || 'Admin')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || 'AD';

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post('/v1/admin/logout');
    } catch (e) {}
    localStorage.removeItem('gpo_admin_token');
    localStorage.removeItem('gpo_admin_user');
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={1.25} sx={{ p: 0.5, alignItems: 'center', minWidth: 0 }}>
          <Avatar alt={adminData.name} sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>
            {initials}
          </Avatar>
          {/* Em ecrãs pequenos mostra só o avatar */}
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600, maxWidth: 200 }}
          >
            {adminData.name}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: 300, maxWidth: 'calc(100vw - 32px)' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}>{initials}</Avatar>
                      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                        <Tooltip title={adminData.name} placement="top-start" enterDelay={500}>
                          {/* O nome pode ocupar até 2 linhas; o email fica numa linha com "…" */}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word'
                            }}
                          >
                            {adminData.name}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={adminData.email} placement="bottom-start" enterDelay={500}>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {adminData.email}
                          </Typography>
                        </Tooltip>
                        <Box sx={{ pt: 0.75 }}>
                          <Chip
                            icon={<CrownOutlined />}
                            label="Superadmin"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 22, '& .MuiChip-icon': { fontSize: '0.75rem', ml: 0.75 } }}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
                    <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32, color: 'error.main' }}>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="error.main">Terminar Sessão</Typography>} />
                    </ListItemButton>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
